import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Check,
  Link2,
  Loader2,
  LogOut,
  Plus,
  Settings,
  User,
  X,
} from "lucide-react";
import { signOut, useSession } from "@/auth/SessionProvider";
import { useAgents } from "@/components/Agents/AgentsContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { apiGet, apiPost } from "@/lib/apiClient";
import {
  isDashboardOrgSegment,
  readDashboardOrgCookie,
  writeDashboardOrgCookie,
} from "@/lib/dashboardOrg";
import { resetAgentsOrgContextCache } from "@/lib/ensureAgentsOrgContext";
import { cn } from "@/lib/utils";

type OrgRow = {
  orgId: string;
  domain: string;
  companyName: string;
  displayEmail?: string | null;
  role?: string | null;
  status?: "active" | "suspended" | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userLabel: string;
  userImageSrc?: string | null;
  onOpenProfile?: () => void;
};

const ONE_ORG_MANAGEMENT_URL = "https://one.atw.net/organization";
const SEED_ORG_NAME = "demo company";

function displayOrgName(name: string, domain?: string | null): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed.toLowerCase() === SEED_ORG_NAME) {
    return domain?.trim() || "Organization";
  }
  return trimmed;
}

function roleLabel(role: string | null | undefined): string {
  if (!role) return "";
  if (role === "account_owner") return "Organization owner";
  if (role === "super_admin") return "Super Admin";
  return role
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function initials(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase();
}

export function AgentsOrgPanel({
  open,
  onOpenChange,
  userLabel,
  userImageSrc,
  onOpenProfile,
}: Props) {
  const navigate = useNavigate();
  const session = useSession();
  const { org, applyActiveOrg } = useAgents();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"list" | "connect" | "create">("list");

  const [connectEmail, setConnectEmail] = useState("");
  const [connectPassword, setConnectPassword] = useState("");
  const [connectDomain, setConnectDomain] = useState("");
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectProfileSource, setConnectProfileSource] = useState<"global" | "current_org">(
    "global",
  );

  const [createName, setCreateName] = useState("");
  const [createDomain, setCreateDomain] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createProfileSource, setCreateProfileSource] = useState<"global" | "current_org">(
    "global",
  );

  const sessionOrgId = session.data?.user?.orgId?.trim() || "";
  const resolvedOrgId =
    (isDashboardOrgSegment(sessionOrgId) ? sessionOrgId : null) ||
    (org.orgId?.trim() && isDashboardOrgSegment(org.orgId) ? org.orgId.trim() : null) ||
    readDashboardOrgCookie();
  const currentOrg = resolvedOrgId;
  const userRole = session.data?.user?.role?.trim() || "";
  const showOrgManagement =
    userRole === "super_admin" || userRole === "account_owner" || userRole === "Super Admin";

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<{ orgs?: OrgRow[] }>("/api/me/orgs");
      if (res.ok && res.data?.orgs) {
        setOrgs(res.data.orgs);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMode("list");
    void loadOrgs();
  }, [open, loadOrgs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const switchToOrg = async (orgId: string, companyName?: string | null) => {
    if (!isDashboardOrgSegment(orgId)) return;
    // Host-only dashboard-org cookie only. Never POST /api/auth/session from Agents
    // (proxied Set-Cookie can clear the shared .atw.net SSO session).
    writeDashboardOrgCookie(orgId);
    resetAgentsOrgContextCache();
    applyActiveOrg(orgId, companyName || null);
    onOpenChange(false);
  };

  const submitConnect = async () => {
    setConnectBusy(true);
    setConnectError(null);
    try {
      const res = await apiPost<{ org?: { orgId: string; companyName?: string }; error?: string }>(
        "/api/me/orgs/connect",
        {
          email: connectEmail.trim(),
          password: connectPassword,
          organizationDomain: connectDomain.trim() || undefined,
          profileSource: connectProfileSource,
          sourceOrgId: currentOrg || undefined,
        },
      );
      if (!res.ok || !res.data?.org?.orgId) {
        setConnectError(res.error || res.data?.error || "Could not connect that organization.");
        return;
      }
      setConnectEmail("");
      setConnectPassword("");
      setConnectDomain("");
      setMode("list");
      await loadOrgs();
      await switchToOrg(res.data.org.orgId, res.data.org.companyName);
    } catch {
      setConnectError("Could not connect that organization.");
    } finally {
      setConnectBusy(false);
    }
  };

  const submitCreate = async () => {
    setCreateBusy(true);
    setCreateError(null);
    try {
      const res = await apiPost<{ org?: { orgId: string; companyName?: string }; error?: string }>(
        "/api/me/orgs/create",
        {
          companyName: createName.trim(),
          domain: createDomain.trim(),
          profileSource: createProfileSource,
          sourceOrgId: currentOrg || undefined,
        },
      );
      if (!res.ok || !res.data?.org?.orgId) {
        setCreateError(res.error || res.data?.error || "Could not create that organization.");
        return;
      }
      setCreateName("");
      setCreateDomain("");
      setMode("list");
      await loadOrgs();
      await switchToOrg(res.data.org.orgId, res.data.org.companyName);
    } catch {
      setCreateError("Could not create that organization.");
    } finally {
      setCreateBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/50"
        aria-label="Close organization panel"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="org-sheet-title"
        className="relative flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-900"
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-4 dark:border-white/10">
          <div>
            <h2 id="org-sheet-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Organization
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Work in one tenant at a time. Connect other organizations you belong to and switch
              between them without signing in again.
            </p>
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/10 dark:bg-white/5">
            {userImageSrc ? (
              <img
                src={userImageSrc}
                alt=""
                className="size-10 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
                {initials(userLabel)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-gray-900 dark:text-white">{userLabel}</p>
              {currentOrg ? (
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  Current org id: <span className="font-mono">{currentOrg}</span>
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">No org in session</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
                aria-label="Agents settings"
                onClick={() => {
                  onOpenChange(false);
                  navigate("/settings");
                }}
              >
                <Settings className="size-5" />
              </button>
              <ThemeToggle />
              {onOpenProfile ? (
                <button
                  type="button"
                  className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
                  aria-label="Your profile"
                  onClick={() => {
                    onOpenChange(false);
                    onOpenProfile();
                  }}
                >
                  <User className="size-5" />
                </button>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            className="mt-3 flex w-full items-center gap-2 rounded-md border border-gray-200 px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-white/10 dark:text-red-400 dark:hover:bg-red-950/30"
            onClick={() => {
              onOpenChange(false);
              signOut();
            }}
          >
            <LogOut className="size-4 shrink-0" aria-hidden />
            Sign out
          </button>

          {mode === "list" ? (
            <div className="mt-6 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Switch organization
                </p>
                {showOrgManagement ? (
                  <a
                    href={ONE_ORG_MANAGEMENT_URL}
                    onClick={() => onOpenChange(false)}
                    className="shrink-0 text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    Organization management
                  </a>
                ) : null}
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="size-8 animate-spin text-gray-400" />
                </div>
              ) : orgs.length === 0 ? (
                currentOrg || org.name ? (
                  <ul className="space-y-1">
                    <li>
                      <div className="flex w-full items-center gap-3 rounded-md border border-sky-300 bg-sky-50 px-3 py-2.5 text-left text-sm dark:border-sky-800 dark:bg-sky-950/40">
                        <Building2 className="size-4 shrink-0 text-gray-500" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-gray-900 dark:text-white">
                            {displayOrgName(org.name)}
                            {session.data?.user?.role ? (
                              <span className="ml-2 text-xs font-normal text-gray-500">
                                {roleLabel(session.data.user.role)}
                              </span>
                            ) : null}
                          </span>
                          {currentOrg ? (
                            <span className="block truncate font-mono text-xs text-gray-500">
                              {currentOrg}
                            </span>
                          ) : null}
                          {session.data?.user?.email ? (
                            <span className="block truncate text-xs text-gray-500">
                              as {session.data.user.email}
                            </span>
                          ) : null}
                        </span>
                        <Check className="size-4 shrink-0 text-sky-600" />
                      </div>
                    </li>
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No organization is assigned to your profile yet.
                  </p>
                )
              ) : (
                <ul className="space-y-1">
                  {orgs
                    .filter((a) => a.orgId && isDashboardOrgSegment(a.orgId))
                    .map((a) => {
                      const oid = a.orgId;
                      const active = currentOrg === oid;
                      return (
                        <li key={oid}>
                          <button
                            type="button"
                            onClick={() => void switchToOrg(oid, a.companyName)}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-colors",
                              active
                                ? "border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40"
                                : "border-transparent hover:bg-gray-100 dark:hover:bg-white/5",
                            )}
                          >
                            <Building2 className="size-4 shrink-0 text-gray-500" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-gray-900 dark:text-white">
                                {displayOrgName(a.companyName || a.domain, a.domain)}
                                {a.role ? (
                                  <span className="ml-2 text-xs font-normal text-gray-500">
                                    {roleLabel(a.role)}
                                  </span>
                                ) : null}
                              </span>
                              <span className="block truncate font-mono text-xs text-gray-500">
                                {a.domain} · {oid}
                              </span>
                              {a.displayEmail ? (
                                <span className="block truncate text-xs text-gray-500">
                                  as {a.displayEmail}
                                </span>
                              ) : null}
                            </span>
                            {active ? <Check className="size-4 shrink-0 text-sky-600" /> : null}
                          </button>
                        </li>
                      );
                    })}
                </ul>
              )}

              <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-4 dark:border-white/10">
                <button
                  type="button"
                  className="flex items-center justify-start rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/5"
                  onClick={() => {
                    setConnectError(null);
                    setMode("connect");
                  }}
                >
                  <Link2 className="mr-2 size-4" />
                  Connect an organization
                </button>
                <button
                  type="button"
                  className="flex items-center justify-start rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/5"
                  onClick={() => {
                    setCreateError(null);
                    setMode("create");
                  }}
                >
                  <Plus className="mr-2 size-4" />
                  Create organization
                </button>
              </div>
            </div>
          ) : mode === "connect" ? (
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setMode("list")}
                className="flex items-center gap-1 self-start text-xs font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              >
                <ArrowLeft className="size-3.5" /> Back
              </button>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Connect an organization
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Sign in once with the account you use for the other organization. After that you can
                switch to it instantly, and you&apos;ll appear under that email while working there.
              </p>
              <label className="block text-sm">
                <span className="font-medium">Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={connectEmail}
                  onChange={(e) => setConnectEmail(e.target.value)}
                  placeholder="you@other-org.com"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={connectPassword}
                  onChange={(e) => setConnectPassword(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Organization domain (optional)</span>
                <input
                  value={connectDomain}
                  onChange={(e) => setConnectDomain(e.target.value)}
                  placeholder="other-org.com"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Profile for this organization</span>
                <select
                  value={connectProfileSource}
                  onChange={(e) =>
                    setConnectProfileSource(
                      e.target.value === "current_org" ? "current_org" : "global",
                    )
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                >
                  <option value="global">Use Global Profile (recommended)</option>
                  <option value="current_org" disabled={!currentOrg}>
                    Copy from current organization
                  </option>
                </select>
              </label>
              {connectError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{connectError}</p>
              ) : null}
              <button
                type="button"
                className="mt-1 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                disabled={connectBusy || !connectEmail.trim() || !connectPassword}
                onClick={() => void submitConnect()}
              >
                {connectBusy ? <Loader2 className="mr-2 inline size-4 animate-spin" /> : null}
                Verify and connect
              </button>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setMode("list")}
                className="flex items-center gap-1 self-start text-xs font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              >
                <ArrowLeft className="size-3.5" /> Back
              </button>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Create organization</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                You&apos;ll be the owner of the new organization and can switch to it right away.
              </p>
              <label className="block text-sm">
                <span className="font-medium">Company name</span>
                <input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Acme, Inc."
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Organization domain</span>
                <input
                  value={createDomain}
                  onChange={(e) => setCreateDomain(e.target.value)}
                  placeholder="acme.com"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Profile for this organization</span>
                <select
                  value={createProfileSource}
                  onChange={(e) =>
                    setCreateProfileSource(
                      e.target.value === "current_org" ? "current_org" : "global",
                    )
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5"
                >
                  <option value="global">Use Global Profile (recommended)</option>
                  <option value="current_org" disabled={!currentOrg}>
                    Copy from current organization
                  </option>
                </select>
              </label>
              {createError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{createError}</p>
              ) : null}
              <button
                type="button"
                className="mt-1 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                disabled={createBusy || !createName.trim() || !createDomain.trim()}
                onClick={() => void submitCreate()}
              >
                {createBusy ? <Loader2 className="mr-2 inline size-4 animate-spin" /> : null}
                Create organization
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
